import { Controller } from "@hotwired/stimulus"

// Brand kit restore link — Phase E6 follow-up.
//
// Renders next to a wizard control when the org has a brand-kit value
// AND the currently-saved (or in-progress) form value diverges from it.
// Click → write the kit value onto the target input + dispatch `input`
// + `change` events so live-preview controllers and the form persist
// the restored value the same way they would a manual edit.
//
// Mounted on a `<button type="button">` element with:
//   data-controller="brand-kit-restore"
//   data-brand-kit-restore-input-selector-value="<CSS selector for the form input>"
//   data-brand-kit-restore-kit-value-value="<the brand-kit value to write>"
//   data-action="click->brand-kit-restore#restore"
//
// Why a CSS selector value vs a target?
//   The form input the link controls usually lives in a parent
//   ViewComponent (Common::TypographyPickerComponent /
//   Common::TextTreatmentComponent), not a child of the link's wrapper.
//   Using a target requires identical controller scope, which would
//   force us to mount this controller on the entire picker. A selector
//   keeps the controller surface small and lets each picker drop in
//   one link per control without restructuring.
export default class extends Controller {
  static values = {
    inputSelector: String,
    kitValue: String,
  }

  restore(event) {
    if (event) event.preventDefault()

    const selector = this.inputSelectorValue
    if (!selector) return

    const input = document.querySelector(selector)
    if (!input) return

    const kit = this.kitValueValue

    if (input.type === "checkbox") {
      // Booleans (text_shadow) are persisted as "1"/"0"/true/false in
      // the kit; coerce to checked state.
      input.checked = ["1", "true", "on", true].includes(kit)
    } else if (input.type === "radio") {
      // Radio groups: programmatic `checked = true` doesn't auto-
      // uncheck siblings the way user clicks do, so explicitly clear
      // every other radio with the same name first. We iterate
      // `getElementsByName(name)` which sidesteps needing a CSS
      // selector escape (CSS.escape isn't polyfilled in jsdom).
      const name = input.name
      if (!name) return
      const candidates = document.getElementsByName(name)
      let matched = null
      for (const r of candidates) {
        if (r.tagName !== "INPUT" || r.type !== "radio") continue
        if (String(r.value) === String(kit)) {
          r.checked = true
          matched = r
        } else {
          r.checked = false
        }
      }
      if (matched) {
        matched.dispatchEvent(new Event("input", { bubbles: true }))
        matched.dispatchEvent(new Event("change", { bubbles: true }))
      }
      return
    } else {
      input.value = kit
    }

    input.dispatchEvent(new Event("input", { bubbles: true }))
    input.dispatchEvent(new Event("change", { bubbles: true }))
  }
}
